import { useState, useEffect } from "react";
import { Shield, Users, ArrowRight, Airplay } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PRODUCT } from "@/config/brand";
import { Button } from "@/components/ui/button";

const HeroSection = () => {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  // Defer the heavy decorative SVG layers + blur orbs until after the first
  // paint so the hero text/CTAs become interactive immediately on mobile.
  const [decorReady, setDecorReady] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setRevealed(true));
    const w = window as any;
    const idle: any = w.requestIdleCallback
      ? w.requestIdleCallback(() => setDecorReady(true), { timeout: 600 })
      : setTimeout(() => setDecorReady(true), 200);
    return () => {
      cancelAnimationFrame(t);
      if (w.cancelIdleCallback) w.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, []);

  const stagger = (delayMs: number) => ({
    opacity: revealed ? 1 : 0,
    transform: revealed ? "translateY(0)" : "translateY(18px)",
    transition: `opacity 0.7s ease-out ${delayMs}ms, transform 0.7s ease-out ${delayMs}ms`,
  });

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"

    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-deep to-primary" aria-hidden="true" />
      {decorReady && <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-accent/10 blur-3xl hidden md:block" aria-hidden="true" />}

      {/* Content */}
      <div className="container relative z-10 pt-4 pb-12 lg:pt-6 lg:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Compact dual trust pill */}
          <div
            className="inline-flex items-center gap-1.5 p-1 pl-1.5 rounded-full bg-primary-foreground/[0.06] backdrop-blur-md border border-primary-foreground/15 mb-4"
            style={stagger(150)}
          >
            <button
              onClick={() => navigate("/platform#team-collaboration")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-primary-foreground/85 tracking-wide hover:text-primary-foreground transition-colors"
            >
              <Users className="w-3.5 h-3.5 text-accent" />
              {"Single-Tenant\n·\u00a0AI Isolated Architecture"}
            </button>
            <span className="w-px h-3 bg-primary-foreground/20" />
            <button
              onClick={() => navigate("/security-compliance")}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-primary-foreground/85 tracking-wide hover:text-primary-foreground transition-colors"
            >
              <Shield className="w-3.5 h-3.5 text-accent" />
              Validation-ready · Traceable
            </button>
          </div>

          {/* Headline */}
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-5 leading-[1.08] tracking-tight"
            style={stagger(250)}
          >
            {PRODUCT.name}
            <span className="block text-accent mt-2 text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight">One connected GxP platform for biopharma quality, laboratory and asset operations.</span>
          </h1>

          {/* Sub-headline */}
          <p
            className="text-base md:text-lg text-primary-foreground/70 max-w-2xl mx-auto mb-8 leading-relaxed"
            style={stagger(350)}
          >
            Pharmalify Spine brings eQMS, LIMS and CMMS workflows together, with governed AI assistance and
            regulatory monitoring built in. Your qualified team retains responsibility for review and approval.
          </p>

          {/* Primary CTAs */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
            style={stagger(450)}
          >
            <Button
              size="lg"
              onClick={() => navigate("/contact")}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 h-11 rounded-full shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:-translate-y-0.5 transition-all duration-300 group"
            >
              {PRODUCT.cta.primary}
              <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/spine")}
              className="bg-primary-foreground/[0.04] hover:bg-primary-foreground/[0.10] text-primary-foreground border-primary-foreground/25 hover:border-primary-foreground/40 font-semibold px-6 h-11 rounded-full backdrop-blur-sm transition-all duration-300 group"
            >
               <Airplay className="w-4 h-4 mr-1.5 text-accent" />
              Explore Pharmalify Spine
            </Button>
          </div>
        </div>

        {/* Founder slogan — pinned at the very bottom of the hero, just above the edge fade */}
        <p
          className="absolute -bottom-8 inset-x-0 text-center text-xs italic text-primary-foreground/55 tracking-wide px-6 max-w-5xl mx-auto z-10 md:text-base "
          style={stagger(700)}
        >
          Built by Biopharma industry experts who lived the problem.
        </p>
      </div>

      {/* Bottom edge — thin, dark transition that hugs the very bottom so no bright band shows before scroll */}
      <div className="absolute bottom-0 inset-x-0 h-8 bg-gradient-to-b from-transparent to-navy-deep pointer-events-none" />
    </section>
  );
};

export default HeroSection;
